import { strList, nullableStr, bool, type FieldCodec } from '../../lib/viewState';
import { melbourneDate, weekOf } from '../../../shared/dates';
import { OUTREACH_LEAD_STATUSES, OUTREACH_VERTICALS, type OutreachClosedReason, type OutreachLeadRow, type OutreachLeadStatus, type OutreachVertical } from '../../../shared/types';
import { STATUS_LABELS, CLOSED_REASON_LABELS, SCORE_COMPONENTS, LINK_FIELDS } from './constants';
import type { VideoOpportunity, DecisionMaker, PublishedContacts, LeadLink, ScoreComponentRow, OutreachCounterKey, OutreachCounters, RankedLead, OutreachFilters, QuickAction } from './types';

// ── Pure helpers (exported for the tests) ─────────────────────────────────

/** "closed, won" — a closed chip always carries its reason. */
export function statusLabel(lead: OutreachLeadRow): string {
  if (lead.status !== 'closed') return STATUS_LABELS[lead.status];
  return lead.closed_reason ? `closed, ${CLOSED_REASON_LABELS[lead.closed_reason]}` : 'closed';
}

/** Chip tone per the approved list: replied reads accent, the two chasing
    states gold, closed struck through. */
export function statusPillClass(lead: OutreachLeadRow): string {
  switch (lead.status) {
    case 'replied':
      return 'pill accent';
    case 'reached_out':
      return 'pill gold';
    case 'followup':
      return 'pill followup';
    case 'closed':
      return 'pill struck';
    default:
      return 'pill';
  }
}

/** Only a plain http(s) address is safe to hand a click. Dossier links come
    from a crawler, so anything else renders as text instead of a link. */
export function safeUrl(url: string | null | undefined): string | null {
  const trimmed = url?.trim() ?? '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

/** "hawthornmedical.com.au/about" — a source link reads as the page it came
    from, not as a full address. */
export function shortUrl(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

export function parseJson<T>(text: string, fallback: T): T {
  try {
    const parsed = JSON.parse(text) as unknown;
    return parsed === null || parsed === undefined ? fallback : (parsed as T);
  } catch {
    return fallback;
  }
}

export function videoOpportunities(lead: OutreachLeadRow): VideoOpportunity[] {
  const raw = parseJson<unknown>(lead.video_opportunities, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const entry = (item ?? {}) as { service?: unknown; why?: unknown };
      return {
        service: typeof entry.service === 'string' ? entry.service : '',
        why: typeof entry.why === 'string' ? entry.why : '',
      };
    })
    .filter((o) => o.service !== '');
}

export function decisionMaker(lead: OutreachLeadRow): DecisionMaker {
  const raw = parseJson<Record<string, unknown>>(lead.decision_maker, {});
  const text = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value : null);
  return {
    name: text(raw.name),
    role: text(raw.role),
    linkedinUrl: safeUrl(text(raw.linkedinUrl)),
    sourceUrl: safeUrl(text(raw.sourceUrl)),
  };
}

/** The link pills that have a real address, plus the words for the ones the
    crawler never found. */
export function leadLinks(lead: OutreachLeadRow): { pills: LeadLink[]; missing: string[] } {
  const raw = parseJson<Record<string, unknown>>(lead.links, {});
  const pills: LeadLink[] = [];
  const missing: string[] = [];
  for (const field of LINK_FIELDS) {
    const href = safeUrl(typeof raw[field.key] === 'string' ? (raw[field.key] as string) : null);
    if (href) pills.push({ label: field.label, href });
    else missing.push(field.missing);
  }
  return { pills, missing };
}

export function publishedContacts(lead: OutreachLeadRow): PublishedContacts {
  const raw = parseJson<Record<string, unknown>>(lead.contact, {});
  const value = (item: unknown) => {
    const entry = (item ?? {}) as { value?: unknown; sourceUrl?: unknown };
    if (typeof entry.value !== 'string' || entry.value.trim() === '') return null;
    return {
      value: entry.value.trim(),
      sourceUrl: safeUrl(typeof entry.sourceUrl === 'string' ? entry.sourceUrl : null),
    };
  };
  const emails = Array.isArray(raw.emails)
    ? raw.emails.map(value).filter((e): e is { value: string; sourceUrl: string | null } => e !== null)
    : [];
  return {
    emails,
    phone: value(raw.phone),
    manualCheckUrl: safeUrl(typeof raw.manualCheckUrl === 'string' ? raw.manualCheckUrl : null),
  };
}

export function scoreBreakdown(lead: OutreachLeadRow): ScoreComponentRow[] | null {
  const raw = parseJson<unknown>(lead.score_breakdown, null);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const breakdown = raw as Record<string, unknown>;
  const rows = SCORE_COMPONENTS.map((component) => {
    const value = breakdown[component.key];
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const entry = value as Record<string, unknown>;
    if (typeof entry.points !== 'number' || !Number.isFinite(entry.points)
      || typeof entry.why !== 'string') return null;
    return { ...component, points: entry.points, why: entry.why };
  });
  return rows.every((row): row is ScoreComponentRow => row !== null) ? rows : null;
}

/** "Instagram or LinkedIn company page" — plain-words list for the missing
    links line. */
export function joinWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

export function counterKey(lead: OutreachLeadRow): OutreachCounterKey | null {
  if (lead.status === 'closed') return lead.closed_reason === 'won' ? 'closed_won' : null;
  return lead.status;
}

/** The five tiles. The big number is leads touched this Melbourne week (Mon
    to Sun, by updated_at), the small one is all time, one rule for all five
    — so a lead pushed Tuesday and chased Thursday counts once, under
    followup. Closed as not a fit has no tile; it lives on its row chip and
    under the closed filter. */
export function outreachCounters(leads: OutreachLeadRow[], today: string): OutreachCounters {
  const week = weekOf(today);
  const counters: OutreachCounters = {
    new: { week: 0, all: 0 },
    reached_out: { week: 0, all: 0 },
    followup: { week: 0, all: 0 },
    replied: { week: 0, all: 0 },
    closed_won: { week: 0, all: 0 },
  };
  for (const lead of leads) {
    const key = counterKey(lead);
    if (key === null) continue;
    counters[key].all += 1;
    const touched = melbourneDate(lead.updated_at);
    if (touched >= week.start && touched <= week.end) counters[key].week += 1;
  }
  return counters;
}

/** Run rank: shortlist first, then queued, each by score descending, which is
    the order the lead book shipped them in. Ranking happens before the status
    and vertical chips are applied, so filtering leaves gaps in the numbers
    rather than renumbering the list under you. */
export function rankLeads(leads: OutreachLeadRow[]): RankedLead[] {
  return [...leads]
    .sort((a, b) => {
      if (a.verdict !== b.verdict) return a.verdict === 'shortlist' ? -1 : 1;
      if (a.score !== b.score) return b.score - a.score;
      return a.name.localeCompare(b.name);
    })
    .map((lead, i) => ({ lead, rank: i + 1 }));
}

export function filterLeads(ranked: RankedLead[], filters: OutreachFilters): RankedLead[] {
  return ranked.filter(({ lead }) => {
    if (filters.statuses.length > 0 && !filters.statuses.includes(lead.status)) return false;
    if (filters.vertical !== null && lead.vertical !== filters.vertical) return false;
    if (filters.runDate !== null && lead.run_date !== filters.runDate) return false;
    return true;
  });
}

/** The ladder from the approved list: the chip is always the next thing you
    would actually do. You email, then you chase, then they come back. A
    closed lead reopens at reached out, since it has already been through the
    top of the ladder, and the PATCH clears its reason on the way. */
export function quickAction(lead: OutreachLeadRow): QuickAction {
  switch (lead.status) {
    case 'new':
      return { kind: 'status', label: 'reached out', status: 'reached_out' };
    case 'reached_out':
      return { kind: 'status', label: 'followup', status: 'followup' };
    case 'followup':
      return { kind: 'status', label: 'replied', status: 'replied' };
    case 'replied':
      return lead.promoted_lead_id
        ? { kind: 'promoted', label: 'in Leads ↗', to: `/leads/${lead.promoted_lead_id}` }
        : { kind: 'promote', label: 'promote' };
    default:
      return { kind: 'status', label: 'reopen', status: 'reached_out' };
  }
}

/** PATCH body for a status click. Closing carries its reason; every other
    status is written bare, and the API clears the reason on the way out of
    closed. */
export function statusPatch(
  status: OutreachLeadStatus,
  closedReason: OutreachClosedReason | null = null,
): Record<string, unknown> {
  return status === 'closed' ? { status, closed_reason: closedReason } : { status };
}

// ── Page ─────────────────────────────────────────────────────────────────

export const outreachViewCodec = {
  statuses: strList('status', OUTREACH_LEAD_STATUSES),
  vertical: { ...nullableStr('vertical'), dec: (raw: string) => OUTREACH_VERTICALS.includes(raw as OutreachVertical) ? raw as OutreachVertical : undefined } as FieldCodec<OutreachVertical | null>,
  // null is automatic/newest; the explicit sentinel 'all' pins every run.
  run: { ...nullableStr('run'), dec: (raw: string) => raw === 'all' || /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : undefined },
  shortlistOpen: bool('shortlist', true), queuedOpen: bool('queued'),
};

/** Automatic selection is local; only an explicit choice persists a run. */
export function outreachRunParam(runDate: string | null, userPinned: boolean): string | null {
  return userPinned ? runDate ?? 'all' : null;
}
